import { keepPreviousData, useQuery } from "@tanstack/react-query";

import { api } from "../../api/client";
import { type DeploymentParams, useUi } from "../../state/ui";

declare module "../../state/ui" {
  interface DeploymentParams {
    /** Aperçu d'un brouillon modifié des Données initiales (registre K19) : dossier enregistré dont il part. Le
     *  serveur reprend ses déplacements, son fuseau personnalisé et ses missions choisies, conservés selon les
     *  règles du changement des Données initiales (docs/14 §3.8), et dit dans `notices` ce qui serait effacé. */
    base?: string | null;
  }
}

/** OPO qui compte pour le déploiement : celui affiché en CENTAC, 1 aux autres niveaux. */
export function effectiveOpo(params: DeploymentParams | null, opo: number): number {
  return params?.level === "CENTAC" ? opo : 1;
}

/** Clé du cache du déploiement affiché (remplacée directement après un déplacement). */
export function deploymentKey(params: DeploymentParams | null, opo: number) {
  return ["deployment", params, effectiveOpo(params, opo)] as const;
}

/** Déploiement T1/T2/T3 des paramètres en cours (calculé par le serveur, partagé par la carte,
 *  les calques et la fiche d'objet). Pour un dossier enregistré et non modifié, c'est celui du dossier,
 *  déplacements manuels compris ; sinon un aperçu du brouillon, qui part de l'état du dossier enregistré
 *  (`base`) quand le brouillon le modifie. En CENTAC, celui de l'OPO affiché. Celui d'un dossier enregistré est
 *  relu au retour sur l'onglet (modification venue d'un autre poste). */
export function useDeployment() {
  const params = useUi((s) => s.deploymentParams);
  const opo = effectiveOpo(params, useUi((s) => s.opo));
  return useQuery({
    queryKey: deploymentKey(params, opo),
    queryFn: () =>
      params!.exercise_id ? api.exerciseDeployment(params!.exercise_id, opo) : api.deployment({ ...params!, opo }),
    enabled: Boolean(params),
    placeholderData: keepPreviousData,
    retry: false,
    staleTime: 60_000,
    // Dossier enregistré : relu au retour sur l'onglet, où un autre poste a pu déplacer des unités (l'OPO, relu de
    // même, montrerait sinon un dispositif que la carte de ce poste n'a pas encore).
    refetchOnWindowFocus: params?.exercise_id ? "always" : false,
  });
}
